/* eslint-disable camelcase, security/detect-object-injection */
import React, { FC, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import { useNotify, useTranslate } from 'react-admin'
import { themeColor, xxllncColor } from '../../layout/theme/color'
import type {
  PreviewItem,
  PreviewResult,
  PreviewSection,
} from '../../types/apiRequests'
import { executeImport, ImportItemDecision, ImportSelections } from './api'

type SectionKey = 'applications' | 'skills' | 'mcp_servers' | 'workflows'
const SECTION_KEYS: SectionKey[] = ['applications', 'skills', 'mcp_servers', 'workflows']

interface ItemState {
  enabled: boolean
  decision: ImportItemDecision
}

type SectionState = Record<string, ItemState>
type AllState = Record<SectionKey, SectionState>

const previewSection = (preview: PreviewResult, key: SectionKey): PreviewSection => {
  switch (key) {
    case 'applications': return preview.applications ?? { items: [] }
    case 'skills': return preview.skills ?? { items: [] }
    case 'mcp_servers': return preview.mcp_servers ?? { items: [] }
    case 'workflows': return preview.workflows ?? { items: [] }
  }
}

const buildInitialState = (preview: PreviewResult): AllState => {
  const sections: AllState = {
    applications: {},
    skills: {},
    mcp_servers: {},
    workflows: {},
  }
  for (const key of SECTION_KEYS) {
    const sec = previewSection(preview, key)
    for (const item of sec.items ?? []) {
      sections[key][item.name] = {
        enabled: true,
        decision: item.default_decision,
      }
    }
  }
  return sections
}

const sectionLabel = (key: SectionKey): string => {
  switch (key) {
    case 'applications':
      return 'portalSettings.importExport.import.sectionApplications'
    case 'skills':
      return 'portalSettings.importExport.import.sectionSkills'
    case 'mcp_servers':
      return 'portalSettings.importExport.import.sectionMcpServers'
    case 'workflows':
      return 'portalSettings.importExport.import.sectionWorkflows'
  }
}

const ItemRow: FC<{
  item: PreviewItem
  state: ItemState
  setState: (next: ItemState) => void
}> = ({ item, state, setState }) => {
  const translate = useTranslate()
  const isExisting = item.status === 'exists'
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1.25 }}>
      <Switch
        size="small"
        checked={state.enabled}
        onChange={(_, v) => setState({ ...state, enabled: v })}
      />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: themeColor.slate90,
              maxWidth: 360,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.display_name}
          </Typography>
          <Chip
            size="small"
            label={
              isExisting
                ? translate('portalSettings.importExport.import.chipExists')
                : translate('portalSettings.importExport.import.chipNew')
            }
            sx={{
              bgcolor: isExisting ? themeColor.slate05 : xxllncColor.opGreen10,
              color: isExisting ? themeColor.slate60 : xxllncColor.green100,
              border: `1px solid ${
                isExisting ? themeColor.slate10 : xxllncColor.opGreen20
              }`,
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              height: 20,
              ['& .MuiChip-label']: { px: 1 },
            }}
          />
          {(item.warnings ?? []).length > 0 && (
            <Tooltip title={(item.warnings ?? []).join('\n')} placement="top">
              <WarningAmberIcon
                sx={{ fontSize: 16, color: xxllncColor.orange100 }}
              />
            </Tooltip>
          )}
        </Stack>
        <Typography
          variant="caption"
          sx={{ color: themeColor.slate50, display: 'block', mt: 0.25 }}
        >
          {item.name}
          {(item.dependency_names ?? []).length > 0 && (
            <>
              {' · '}
              {translate('portalSettings.importExport.import.dependsOn', {
                names: (item.dependency_names ?? []).join(', '),
              })}
            </>
          )}
        </Typography>
      </Box>
      <Select
        size="small"
        value={state.decision}
        disabled={!state.enabled}
        onChange={(e) =>
          setState({ ...state, decision: e.target.value as ImportItemDecision })
        }
        sx={{
          minWidth: 130,
          bgcolor: '#FFFFFF',
          ['& .MuiOutlinedInput-input']: { py: '6px' },
        }}
      >
        {!isExisting ? (
          <MenuItem value="create">
            {translate('portalSettings.importExport.import.decisionCreate')}
          </MenuItem>
        ) : (
          [
            <MenuItem key="skip" value="skip">
              {translate('portalSettings.importExport.import.decisionSkip')}
            </MenuItem>,
            <MenuItem key="overwrite" value="overwrite">
              {translate('portalSettings.importExport.import.decisionOverwrite')}
            </MenuItem>,
          ]
        )}
      </Select>
    </Stack>
  )
}

const SectionBlock: FC<{
  sectionKey: SectionKey
  preview: PreviewSection
  state: SectionState
  onChange: (sectionKey: SectionKey, name: string, next: ItemState) => void
  onToggleAll: (sectionKey: SectionKey) => void
}> = ({ sectionKey, preview, state, onChange, onToggleAll }) => {
  const translate = useTranslate()
  const items = preview.items ?? []
  if (items.length === 0) return null
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.8,
            color: themeColor.slate60,
          }}
        >
          {translate(sectionLabel(sectionKey))} ({items.length})
        </Typography>
        <Button
          size="small"
          onClick={() => onToggleAll(sectionKey)}
          sx={{ fontSize: 12, color: themeColor.slate60 }}
        >
          {translate('portalSettings.importExport.import.toggleAll')}
        </Button>
      </Stack>
      <Box
        sx={{
          border: `1px solid ${themeColor.slate10}`,
          borderRadius: '12px',
          px: 2,
          bgcolor: '#FFFFFF',
        }}
      >
        {items.map((item, idx) => (
          <Box key={item.name}>
            {idx > 0 && <Divider sx={{ borderColor: themeColor.slate05 }} />}
            <ItemRow
              item={item}
              state={state[item.name] || { enabled: true, decision: item.default_decision }}
              setState={(next) => onChange(sectionKey, item.name, next)}
            />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

const ImportPreviewDialog: FC<{
  file: File | null
  preview: PreviewResult | null
  onClose: (didImport: boolean) => void
}> = ({ file, preview, onClose }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [state, setState] = useState<AllState | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setState(preview ? buildInitialState(preview) : null)
  }, [preview])

  const open = !!file && !!preview && !!state

  const onChange = (sectionKey: SectionKey, name: string, next: ItemState) => {
    setState((prev) => {
      if (!prev) return prev
      return { ...prev, [sectionKey]: { ...prev[sectionKey], [name]: next } }
    })
  }

  const onToggleAll = (sectionKey: SectionKey) => {
    setState((prev) => {
      if (!prev) return prev
      const allOn = Object.values(prev[sectionKey]).every((s) => s.enabled)
      const next: SectionState = {}
      for (const [name, s] of Object.entries(prev[sectionKey])) {
        next[name] = { ...s, enabled: !allOn }
      }
      return { ...prev, [sectionKey]: next }
    })
  }

  const itemCount = useMemo(() => {
    if (!state) return 0
    let count = 0
    for (const key of SECTION_KEYS) {
      for (const itemState of Object.values(state[key])) {
        if (itemState.enabled && itemState.decision !== 'skip') count++
      }
    }
    return count
  }, [state])

  const totalItems = useMemo(() => {
    if (!preview) return 0
    return SECTION_KEYS.reduce(
      (sum, key) =>
        sum + (previewSection(preview, key).items ?? []).length,
      0,
    )
  }, [preview])

  const buildSelections = (): ImportSelections => {
    const decisions: ImportSelections['decisions'] = {}
    if (!state) return { decisions }
    for (const key of SECTION_KEYS) {
      decisions[key] = {}
      for (const [name, itemState] of Object.entries(state[key])) {
        decisions[key][name] = itemState.enabled ? itemState.decision : 'skip'
      }
    }
    return { decisions }
  }

  const handleImport = async () => {
    if (!file) return
    setBusy(true)
    try {
      const result = await executeImport(file, buildSelections())
      const counts: Record<string, number> = result.counts ?? {}
      notify(
        translate('portalSettings.importExport.import.successCounts', {
          created: counts.created || 0,
          updated: counts.updated || 0,
          skipped: counts.skipped || 0,
          failed: counts.failed || 0,
        }),
        { type: counts.failed ? 'warning' : 'success' },
      )
      onClose(true)
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Import failed', {
        type: 'error',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : () => onClose(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}
    >
      {preview && state && (
        <>
          <DialogTitle
            sx={{
              pb: 1,
              borderBottom: `1px solid ${themeColor.slate10}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: themeColor.slate90 }}>
              {translate('portalSettings.importExport.import.previewTitle')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: themeColor.slate50,
                mt: 0.5,
                fontSize: 12,
              }}
            >
              {file?.name} · {preview.source_instance} · {preview.exported_by}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, bgcolor: themeColor.slate05 }}>
            <Typography variant="body2" sx={{ color: themeColor.slate60, mb: 2.5 }}>
              {translate('portalSettings.importExport.import.previewIntro')}
            </Typography>
            {totalItems === 0 ? (
              <Typography variant="body2" sx={{ color: themeColor.slate60 }}>
                {translate('portalSettings.importExport.import.emptyBundle')}
              </Typography>
            ) : (
              <Stack spacing={2.5}>
                {SECTION_KEYS.map((key) => (
                  <SectionBlock
                    key={key}
                    sectionKey={key}
                    preview={previewSection(preview, key)}
                    state={state[key]}
                    onChange={onChange}
                    onToggleAll={onToggleAll}
                  />
                ))}
                {(preview.global_warnings ?? []).length > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: xxllncColor.opOrange20,
                      borderRadius: '12px',
                      border: `1px solid ${xxllncColor.orange40}`,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      {translate('portalSettings.importExport.import.warningHeader')}
                    </Typography>
                    {(preview.global_warnings ?? []).map((w) => (
                      <Typography key={w} variant="body2">
                        {w}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              pt: 1.5,
              borderTop: `1px solid ${themeColor.slate10}`,
            }}
          >
            <Button onClick={() => onClose(false)} disabled={busy}>
              {translate('portalSettings.importExport.import.cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUploadOutlinedIcon />}
              disabled={busy || itemCount === 0}
              onClick={() => {
                void handleImport()
              }}
            >
              {translate('portalSettings.importExport.import.executeButton', {
                count: itemCount,
              })}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default ImportPreviewDialog
